<template>
  <div>
    <!-- Header del Diseño -->
    <strong
      class="text-sm font-bold text-text-main mb-4 flex items-center gap-2"
    >
      <vue-icon name="tune" class="text-primary text-[20px]" />
      {{ transPeralbaPro.item490 }}
    </strong>
    <!-- Contenedor Lógico -->
    <div
      class="space-y-4"
      v-if="selectedProduct.parent_id && allProducts.length > 1"
    >
      <!-- Loop de Variantes -->
      <div v-for="(variation, index) in variations" :key="index">
        <!-- CASO 1: Atributos generales (Select Dropdown) -->
        <template v-if="variation.name.toLowerCase() != 'color'">
          <label
            class="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1.5 block"
          >
            {{ variation.name }}
          </label>
          <div class="relative">
            <!-- Select con estilo del diseño -->
            <select
              v-if="variation.available_values"
              class="w-full appearance-none bg-white border border-border rounded-lg px-3 py-2 pr-8 text-sm text-text-main font-medium focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all cursor-pointer hover:border-primary/50"
              :id="'select-atrribute-' + variation.id"
              @change="onChange(variation)"
              v-model="variation.selected_variation"
            >
              <option
                v-for="(available_value, index2) in variation.available_values"
                :key="index2"
                :value="available_value"
                :class="{
                  'text-gray-400': !checkAvailability(
                    variation,
                    available_value,
                  ),
                }"
              >
                {{ available_value.value }}
              </option>
            </select>
            <!-- Texto plano si no hay valores disponibles -->
            <span
              v-else
              class="text-sm font-medium text-text-main px-3 py-2 block border border-transparent"
            >
              {{ variation.value }}
            </span>
          </div>
        </template>
        <!-- CASO 2: Colores (Círculos) -->
        <template v-else>
          <div class="mb-1.5 flex items-center gap-2">
            <label
              class="text-xs font-semibold text-text-secondary uppercase tracking-wide"
            >
              {{ variation.name }}:
            </label>
            <strong
              class="text-xs font-bold text-text-main"
              v-if="variation.selected_variation"
            >
              {{ variation.selected_variation.value }}
            </strong>
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(available_value, index2) in variation.available_values"
              :key="index2"
              @click="onChange(variation, available_value)"
              class="group relative w-6 h-6 rounded-full cursor-pointer flex items-center justify-center transition-all duration-200"
              :class="[
                variation.selected_variation &&
                variation.selected_variation.value_id ==
                  available_value.value_id
                  ? 'ring-2 ring-primary ring-offset-2'
                  : 'hover:ring-2 hover:ring-border hover:ring-offset-1',

                !checkAvailability(variation, available_value)
                  ? 'opacity-40 grayscale'
                  : '',
              ]"
            >
              <!-- Círculo de color -->
              <div
                class="w-full h-full rounded-full border border-gray-200 shadow-sm"
                v-bind:style="{'background-color': available_value.info}"
              ></div>
              <div
                v-if="!checkAvailability(variation, available_value)"
                class="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
              >
                <div class="absolute inset-0 bg-black/10 rounded-full"></div>
                <vue-icon
                  name="close"
                  class="text-white drop-shadow-md text-[18px] font-bold"
                  style="text-shadow: 0px 0px 2px rgba(0,0,0,0.5);"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Variations",
  emits: ["update-other-variations", "find-and-set-product"],
  props: {
    selectedProduct: {
      type: Object,
      required: true,
    },
    allProducts: {
      type: Array,
      required: true,
      default: () => [],
    },
    variations: {
      type: Array,
      required: true,
      default: () => [],
    },
    transPeralbaPro: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  mounted() {
    this.initializeSelectedVariations();
  },
  watch: {
    selectedProduct: {
      immediate: true,
      handler() {
        this.initializeSelectedVariations();
      },
    },
  },
  methods: {
    initializeSelectedVariations() {
      if (!this.selectedProduct || !this.variations.length) return;

      const currentProductCode = this.selectedProduct.id; // o el campo que uses como código

      this.variations.forEach((variation) => {
        if (!variation.available_values) return;

        const matchingValue = variation.available_values.find(
          (val) =>
            val.product_codes && val.product_codes.includes(currentProductCode),
        );

        if (matchingValue) {
          variation.selected_variation = matchingValue;
        }
      });
    },
    onChange(variation, value = null) {
      if (value) {
        variation.selected_variation = value;
      }

      const arraysOfCodes = this.variations.map((v) => {
        return v.selected_variation && v.selected_variation.product_codes
          ? v.selected_variation.product_codes
          : [];
      });

      let possibleProducts = arraysOfCodes.reduce((a, b) =>
        a.filter((c) => b.includes(c)),
      );
      let targetProductCode = null;

      if (possibleProducts.length > 0) {
        targetProductCode = possibleProducts[0];
      } else {
        const priorityVariationValue = value || variation.selected_variation;

        if (
          priorityVariationValue.product_codes &&
          priorityVariationValue.product_codes.length > 0
        ) {
          targetProductCode = priorityVariationValue.product_codes[0];

          // ATENCIÓ: Com que aquests mètodes estan al component pare,
          // la forma correcta a Vue 2 és emetre un esdeveniment cap al pare.
          this.$emit(
            "update-other-variations",
            targetProductCode,
            variation.id,
          );
        }
      }

      if (targetProductCode) {
        // El mateix aquí: emetem al pare perquè faci la cerca
        this.$emit("find-and-set-product", targetProductCode);
      }
    },

    checkAvailability(currentVariation, candidateValue) {
      const candidateCodes = candidateValue.product_codes || [];

      const otherVariationsCodes = this.variations
        .filter((v) => v.id !== currentVariation.id && v.selected_variation)
        .map((v) => v.selected_variation.product_codes || []);

      if (otherVariationsCodes.length === 0) return true;

      const currentContextCodes = otherVariationsCodes.reduce((a, b) =>
        a.filter((c) => b.includes(c)),
      );

      const isCompatible = candidateCodes.some((code) =>
        currentContextCodes.includes(code),
      );

      return isCompatible;
    },
  },
};
</script>
